/* eslint-disable camelcase */

import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import type { IconName } from 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Components/vm-label/vm-label';
import 'Components/atoms/vm-profile-image/vm-profile-image';
import 'Components/atoms/vm-video-comment-attachment-link/vm-video-comment-attachment-link';
import 'Components/atoms/vm-video-comment-attachment-thumbnail/vm-video-comment-attachment-thumbnail';
import 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import { removeThumbsupComment, thumbsupComment, type CommentAttachment, type CommentData } from 'Api/comments';
import { getAgo } from 'Utils/text';
import Styles from './vm-video-comments-list-item.scss';

/**
 * A single comment row inside a `<vm-video-comments-list>`. Renders the
 * author block (profile image, name, a "Presenter" label when the author is
 * credited on the video, relative date), the comment body,
 * any image / document attachments, and an action footer with Reply and
 * Thumbs-Up. Image attachments render as
 * `<vm-video-comment-attachment-thumbnail>` chips and open in the
 * page-level `<vm-image-lightbox>` via the `vm-image-lightbox:open` event;
 * non-image attachments render as `<vm-video-comment-attachment-link>`
 * chips and open in a new tab.
 *
 * Used recursively to render nested replies via the `children` array on
 * the bound `comment`. Replies indent (see `:host([is-reply])` styles).
 *
 * Thumbs-up is optimistic — local state updates immediately and rolls
 * back on API failure.
 *
 * Reply behavior is viewport-dependent:
 * - Desktop: opens its OWN inline reply composer (`_replyOpen` state).
 *   Multiple items can have their composers open concurrently. Cancel
 *   closes locally. On successful submit the organism dispatches
 *   `vm-video-comment-reply:posted` and this item self-closes.
 * - Mobile: dispatches `vm-video-comment-reply:select` for the organism to
 *   open the bottom-sheet composer with a "Replying to <Name>" chip.
 *
 * @csspart author - Author name link to the author's profile. Style with `::part(author)`.
 */
@customElement('vm-video-comments-list-item')
export class VmVideoCommentsListItem extends LitElement {
    static override styles = Styles;

    @property({ type: Object }) comment: CommentData | null = null;
    @property({ type: Boolean }) dataAddCommentsPerm = false;
    @property({ type: String }) videoSlug = '';
    @property({ type: Boolean }) isReply = false;

    /** Session user info; passed to the inline reply composer when it opens. */
    @property({ type: String }) userAvatarSrc = '';
    @property({ type: String }) userAvatarName = '';
    @property({ type: String }) userProfileUrl = '';

    /** Whether this row's inline reply composer is currently open (desktop only). */
    @state() private _replyOpen = false;

    /** Cached from `matchMedia(DESKTOP_BREAKPOINT_QUERY)` + change listener. Kept in sync so `handleReplyClick` doesn't re-query on every click. */
    @state() private _isDesktop = false;

    private _mq: MediaQueryList | null = null;
    private _onMqChange = (e: MediaQueryListEvent) => {
        this._isDesktop = e.matches;
        // Transition off desktop → the mobile flow uses the top composer + chip,
        // so any open inline composer here would render alongside it. Close it.
        if (!e.matches) this._replyOpen = false;
    };

    handleReplyClick() {
        if (!this.comment) return;

        // Mobile: defer to the organism (top composer + chip flow).
        if (!this._isDesktop) {
            const { id, author } = this.comment;
            this.dispatchEvent(
                new CustomEvent('vm-video-comment-reply:select', {
                    detail: { commentId: id, authorName: `${author.first_name} ${author.last_name}` },
                    bubbles: true,
                    composed: true,
                }),
            );
            return;
        }

        // Desktop: open our own inline composer. Idempotent — clicking Reply
        // on an already-open composer is a no-op.
        this._replyOpen = true;
    }

    private _onReplyPosted = (e: Event) => {
        const detail = (e as CustomEvent<{ parentId: number }>).detail;
        if (this.comment && detail.parentId === this.comment.id) this._replyOpen = false;
    };

    private _onInlineComposerCancel = (e: Event) => {
        // Stop the event from bubbling to the organism's host listener — the
        // inline cancel is entirely an item concern. The organism's handler
        // exists for the mobile top-composer chip cancel and would otherwise
        // run a no-op null-out here.
        e.stopPropagation();
        this._replyOpen = false;
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this._mq = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
        this._isDesktop = this._mq.matches;
        this._mq.addEventListener('change', this._onMqChange);
        document.addEventListener('vm-video-comment-reply:posted', this._onReplyPosted);
    }

    override disconnectedCallback(): void {
        this._mq?.removeEventListener('change', this._onMqChange);
        this._mq = null;
        document.removeEventListener('vm-video-comment-reply:posted', this._onReplyPosted);
        super.disconnectedCallback();
    }

    commentPublishedDate(comment: CommentData): string {
        return getAgo(comment.when);
    }

    handleThumbsUpClick() {
        if (!this.comment) return;

        const { id, user_has_thumbed_up, thumbs_up_count } = this.comment;
        const toggle = user_has_thumbed_up ? removeThumbsupComment : thumbsupComment;

        // Mutate in place to avoid triggering parent re-render overwriting child state
        this.comment.user_has_thumbed_up = !user_has_thumbed_up;
        this.comment.thumbs_up_count = !user_has_thumbed_up
            ? (thumbs_up_count || 0) + 1
            : Math.max((thumbs_up_count || 0) - 1, 0);
        this.requestUpdate();

        const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        if (context?.videoId) {
            window.vumedi?.mixpanel?.track('VideoCommentThumbupToggled', {
                video_id: context.videoId,
                video_slug: context.videoSlug,
                channel_id: context.channelId,
                comment_id: id,
                comment_author_id: this.comment.author?.id || null,
                thumbup_enabled: !user_has_thumbed_up,
                template_version: context.templateVersion,
            });
        }

        // Roll back on failure. No Sentry call — `client.ts`'s `beforeError` hook
        // reports it, and reporting here as well would double-count.
        toggle({ videoSlug: this.videoSlug, commentId: id }).catch(() => {
            if (!this.comment) return;
            this.comment.user_has_thumbed_up = user_has_thumbed_up;
            this.comment.thumbs_up_count = thumbs_up_count;
            this.requestUpdate();
        });
    }

    renderAttachments(attachments: CommentAttachment[]) {
        const attachmentItems = attachments.map((attachment) => {
            if (attachment.mimetype.includes('image')) {
                return html`
                    <vm-video-comment-attachment-thumbnail
                        src=${attachment.url}
                        alt="Comment attachment"
                    ></vm-video-comment-attachment-thumbnail>
                `;
            }
            return html`<vm-video-comment-attachment-link href=${attachment.url}></vm-video-comment-attachment-link>`;
        });

        return html`<div class="comment__attachments-list">${attachmentItems}</div>`;
    }

    renderReplies() {
        if (!this.comment?.children?.length) return nothing;

        return html`
            <ul class="comment__replies-list">
                ${this.comment.children.map(
                    (child) => html`
                        <vm-video-comments-list-item
                            .comment=${child}
                            .dataAddCommentsPerm=${this.dataAddCommentsPerm}
                            .videoSlug=${this.videoSlug}
                            .isReply=${true}
                            .userAvatarSrc=${this.userAvatarSrc}
                            .userAvatarName=${this.userAvatarName}
                            .userProfileUrl=${this.userProfileUrl}
                        ></vm-video-comments-list-item>
                    `,
                )}
            </ul>
        `;
    }

    private _renderInlineComposer() {
        if (!this.comment || !this._replyOpen) return nothing;
        return html`
            <vm-video-comment-composer
                class="comment__inline-composer"
                variant="inline"
                video-slug=${this.videoSlug}
                avatar-src=${this.userAvatarSrc}
                avatar-name=${this.userAvatarName}
                avatar-link=${this.userProfileUrl}
                .replyToId=${this.comment.id}
                @vm-comment-reply-cancel=${this._onInlineComposerCancel}
            ></vm-video-comment-composer>
        `;
    }

    /**
     * Action-row button (Thumbs up, Reply). Kept small so the two callsites
     * can differ only in what matters: click handler, optional icon, label.
     */
    private _renderAction(label: string, onClick: () => void, iconName?: IconName) {
        return html`
            <vm-button class="comment__action" variant="ghost" color-scheme="neutral" size="sm" @click=${onClick}>
                ${iconName ? html`<vm-icon-next slot="icon-leading" name=${iconName}></vm-icon-next>` : nothing}
                ${label}
            </vm-button>
        `;
    }

    override render() {
        const comment = this.comment;
        // Bail when the bound comment has no author. Parents that mount this element
        // before their data fetch resolves (see `vm-video-comments`'s mountWithData
        // helper) would otherwise crash on `comment.author.avatar` during the
        // intermediate render.
        if (!comment?.author) return nothing;

        const fullName = `${comment.author.first_name} ${comment.author.last_name}`;
        const thumbsUpLabel = comment.thumbs_up_count > 0 ? `${comment.thumbs_up_count} Thumbs up` : 'Thumbs up';
        const thumbsUpIcon: IconName = comment.user_has_thumbed_up ? 'thumbs-up-solid' : 'thumbs-up';

        return html`
            <li class="comment ${this.isReply ? 'comment--reply' : ''}">
                <div class="comment__inner-wrap">
                    <div class="comment__profile-image">
                        <vm-profile-image
                            .imageSrc=${comment.author.avatar || ''}
                            .imageAlt=${fullName}
                            .userName=${fullName}
                            .link=${comment.author.url}
                        ></vm-profile-image>
                    </div>
                    <div class="comment__content-wrap">
                        <div class="comment__text">
                            <div class="comment__header">
                                <a href=${comment.author.url} part="author" class="comment__author">${fullName}</a>
                                ${comment.is_presenter ? html`<vm-label>Presenter</vm-label>` : nothing}
                                <time class="comment__timestamp" datetime=${comment.when}>
                                    ${this.commentPublishedDate(comment)}
                                </time>
                            </div>
                            <p class="comment__body">${comment.comment}</p>
                            ${comment.attachments?.length ? this.renderAttachments(comment.attachments) : nothing}
                        </div>
                        <div class="comment__footer">
                            ${this._renderAction(thumbsUpLabel, () => this.handleThumbsUpClick(), thumbsUpIcon)}
                            ${this.dataAddCommentsPerm
                                ? this._renderAction('Reply', () => this.handleReplyClick())
                                : nothing}
                        </div>
                        ${this._renderInlineComposer()}
                    </div>
                </div>
                ${this.renderReplies()}
            </li>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comments-list-item': VmVideoCommentsListItem;
    }
    interface HTMLElementEventMap {
        'vm-video-comment-reply:select': CustomEvent<{ commentId: number; authorName: string }>;
    }
}
